import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Play, User, FileText, ArrowRight, Check, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
const MANUALS = [{
  title: "Owner's Manual (Imperial)",
  size: '4.2 MB'
}, {
  title: "Owner's Manual (Metric)",
  size: '4.2 MB'
}, {
  title: 'Quick Start Guide',
  size: '1.8 MB'
}, {
  title: 'Technical Documentation',
  size: '8.5 MB'
}];
const VIDEOS = [{
  title: 'Vehicle Setup',
  duration: '5:20',
  image: 'https://cdn.magicpatterns.com/uploads/h58wUekQZycBGPqmB5effo/682effc0ba43af376950f23b_renders_robeta.jpg'
}, {
  title: 'Maintenance Basics',
  duration: '8:15',
  image: 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg'
}, {
  title: 'Feature Walkthrough',
  duration: '12:30',
  image: 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp'
}, {
  title: 'Off-Grid Systems',
  duration: '6:45',
  image: 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp'
}];
export function ToolsPage() {
  useSEO({
    title: 'Owner Tools & Resources',
    description: 'Everything you need to get the most from your Robeta camper van. Download manuals, watch video guides, and access personalized owner support.'
  });
  const [activeTab, setActiveTab] = useState('manuals');
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-1230">
      {/* 1. HERO SECTION */}
      <section className="container mx-auto px-6 md:px-12 mb-20 md:mb-32" data-id="element-1231">
        <Breadcrumbs items={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Tools & Resources'
      }]} className="mb-8" data-id="element-1232" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-1233">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-1234">
            Owner Support
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-6" data-id="element-1235">
            Tools & Resources
          </h1>
          <p className="text-lg md:text-xl font-light text-robeta-black/60 max-w-2xl leading-relaxed" data-id="element-1236">
            Everything you need to get the most from your Robeta camper van.
            Manuals, guides, and personalized support.
          </p>
        </motion.div>
      </section>

      {/* 2. TOOLS OVERVIEW (Feature Blocks) */}
      <section className="container mx-auto px-6 md:px-12 mb-24 md:mb-32" data-id="element-1237">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1238">
          {[{
          id: 'manuals',
          icon: Download,
          title: 'Download Manuals',
          desc: 'Access digital owner manuals and technical docs.'
        }, {
          id: 'videos',
          icon: Play,
          title: 'Video Guides',
          desc: 'Step-by-step tutorials for your vehicle.'
        }, {
          id: 'assistant',
          icon: User,
          title: 'Personal Assistant',
          desc: 'Direct line to Robeta owner support.'
        }, {
          id: 'brochure',
          icon: FileText,
          title: 'Request Brochure',
          desc: 'Get the latest catalog delivered to you.'
        }].map(tool => <button key={tool.id} onClick={() => {
          const element = document.getElementById(tool.id);
          element?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="group text-left p-8 border border-robeta-black/10 hover:border-robeta-copper transition-all duration-300 bg-white hover:shadow-lg" data-id="element-1239">
              <div className="w-12 h-12 rounded-full bg-robeta-mist/20 flex items-center justify-center mb-6 group-hover:bg-robeta-copper group-hover:text-white transition-colors text-robeta-black" data-id="element-1240">
                <tool.icon className="w-5 h-5" data-id="element-1241" />
              </div>
              <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide mb-3 group-hover:text-robeta-copper transition-colors" data-id="element-1242">
                {tool.title}
              </h3>
              <p className="text-sm text-robeta-black/60 font-light leading-relaxed" data-id="element-1243">
                {tool.desc}
              </p>
            </button>)}
        </div>
      </section>

      {/* 3. DOWNLOAD MANUALS */}
      <section id="manuals" className="py-24 bg-robeta-mist/10" data-id="element-1244">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1245">
          <div className="flex flex-col md:flex-row gap-12 md:gap-24" data-id="element-1246">
            <div className="w-full md:w-1/3" data-id="element-1247">
              <h2 className="text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1248">
                Manuals & Docs
              </h2>
              <p className="text-robeta-black/60 font-light leading-relaxed" data-id="element-1249">
                Keep your Robeta running smoothly with official documentation.
                Updated for 2026 models.
              </p>
            </div>
            <div className="w-full md:w-2/3 grid gap-4" data-id="element-1250">
              {MANUALS.map((manual, i) => <div key={i} className="flex items-center justify-between p-6 bg-white border border-robeta-black/5 hover:border-robeta-copper transition-colors group" data-id="element-1251">
                  <div className="flex items-center gap-4" data-id="element-1252">
                    <FileText className="w-5 h-5 text-robeta-black/30 group-hover:text-robeta-copper transition-colors" data-id="element-1253" />
                    <div data-id="element-1254">
                      <h4 className="text-sm font-bold text-robeta-black uppercase tracking-wide" data-id="element-1255">
                        {manual.title}
                      </h4>
                      <span className="text-xs text-robeta-black/40" data-id="element-1256">
                        PDF • {manual.size}
                      </span>
                    </div>
                  </div>
                  <button className="text-xs font-bold uppercase tracking-widest text-robeta-copper hover:text-robeta-black transition-colors flex items-center gap-2" data-id="element-1257">
                    Download <Download className="w-3 h-3" data-id="element-1258" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. VIDEO GUIDES */}
      <section id="videos" className="py-24 bg-white" data-id="element-1259">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1260">
          <div className="mb-12" data-id="element-1261">
            <h2 className="text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1262">
              Video Guides
            </h2>
            <div className="w-16 h-px bg-robeta-copper" data-id="element-1263" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1264">
            {VIDEOS.map((video, i) => <div key={i} className="group cursor-pointer" data-id="element-1265">
                <div className="relative aspect-video bg-gray-100 mb-4 overflow-hidden" data-id="element-1266">
                  <img src={video.image} alt={video.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1267" />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center" data-id="element-1268">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center pl-1 group-hover:scale-110 transition-transform" data-id="element-1269">
                      <Play className="w-5 h-5 text-robeta-black" data-id="element-1270" />
                    </div>
                  </div>
                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-1 rounded-sm" data-id="element-1271">
                    {video.duration}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-robeta-black uppercase tracking-wide mb-1 group-hover:text-robeta-copper transition-colors" data-id="element-1272">
                  {video.title}
                </h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. PERSONAL ASSISTANT */}
      <section id="assistant" className="py-24 bg-robeta-black text-white" data-id="element-1273">
        <div className="container mx-auto px-6 md:px-12 text-center" data-id="element-1274">
          <User className="w-12 h-12 text-robeta-copper mx-auto mb-8" data-id="element-1275" />
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-6" data-id="element-1276">
            Need Personalized Help?
          </h2>
          <p className="text-white/60 font-light text-lg max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-1277">
            Our Robeta Personal Assistant service connects you directly with a
            specialist who knows your vehicle inside and out.
          </p>
          <Link to="/contact" className="inline-block px-10 py-4 bg-robeta-copper text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors" data-id="element-1278">
            Contact Personal Assistant
          </Link>
        </div>
      </section>

      {/* 6. REQUEST BROCHURE */}
      <section id="brochure" className="py-24 bg-white" data-id="element-1279">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1280">
          <div className="max-w-2xl mx-auto" data-id="element-1281">
            <div className="text-center mb-12" data-id="element-1282">
              <h2 className="text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-4" data-id="element-1283">
                Request a Brochure
              </h2>
              <p className="text-robeta-black/60 font-light" data-id="element-1284">
                Get the complete 2026 catalog delivered to your inbox.
              </p>
            </div>
            <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-1285">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1286">
                <div data-id="element-1287">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-1288">
                    Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-1289" />
                </div>
                <div data-id="element-1290">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-1291">
                    Email
                  </label>
                  <input type="email" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-1292" />
                </div>
                <div data-id="element-1293">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-1294">
                    Region
                  </label>
                  <select className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm appearance-none" data-id="element-1295">
                    <option data-id="element-1296">Select Region...</option>
                    <option data-id="element-1297">Canada</option>
                    <option data-id="element-1298">USA</option>
                    <option data-id="element-1299">Europe</option>
                  </select>
                </div>
                <div data-id="element-1300">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-1301">
                    Model Interest
                  </label>
                  <select className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm appearance-none" data-id="element-1302">
                    <option data-id="element-1303">Select Model...</option>
                    <option data-id="element-1304">Mythos 4 Two</option>
                    <option data-id="element-1305">Apollo</option>
                    <option data-id="element-1306">Helios</option>
                  </select>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1307">
                <input type="checkbox" className="mt-1" data-id="element-1308" />
                <p className="text-xs text-robeta-black/60 leading-relaxed" data-id="element-1309">
                  I consent to receiving the brochure and occasional updates
                  from Robeta.
                </p>
              </div>
              <button className="w-full py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors" data-id="element-1310">
                Send Me the Brochure
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>;
}